var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
// OrbActionHandlers.ts
import * as THREE from 'three';
import * as SpraySystem from '../physics/SpraySystem';
import { actionRegistry, resolveParams } from './OrbActionRegistry';
import { global } from '../../core/GlobalState';
import SceneManager from '../../core/SceneManager';
import { OrbConnectionManager } from '../connections/OrbConnectionManager';
import { connectOrbs } from "./ConnectActions";
import { OrbTypes } from '../../orbs/OrbTypes';
// HELPERS
function getDefaultScene(ctx) {
    var _a, _b;
    // 1) Explicit scene in ctx wins
    if (ctx.scene)
        return ctx.scene;
    // 2) If orb is attached to some Scene (even unmanaged), prefer that
    const parent = (_b = (_a = ctx.orb) === null || _a === void 0 ? void 0 : _a.group) === null || _b === void 0 ? void 0 : _b.parent;
    if (parent && parent instanceof THREE.Scene)
        return parent;
    // 3) Resolve via OrbManager -> SceneManager (managed scenes)
    const sceneName = ctx.manager.getSceneForOrb(ctx.orb);
    if (sceneName)
        return SceneManager.getInstance().getScene(sceneName);
    // 4) Fallback to app's global scene
    return global.scene;
}
function asSprayOptions(params, scene) {
    return {
        sprayCoefficient: params.sprayCoefficient,
        timeGap: params.timeGap,
        baseVelocity: params.baseVelocity,
        drag: params.drag,
        scene,
    };
}
// === New: sprayFromFetcher (name + args -> orbs -> spray) ===
actionRegistry.register('sprayFromFetcher', (ctx, rawParams) => __awaiter(void 0, void 0, void 0, function* () {
    var _a, _b;
    const params = resolveParams(rawParams, ctx);
    const scene = getDefaultScene(ctx);
    if (!scene) {
        console.warn('[sprayFromFetcher] No scene available.');
        return;
    }
    const { fetcher, args, useCache = true } = params;
    if (!fetcher) {
        console.warn('[sprayFromFetcher] Missing "fetcher"');
        return;
    }
    let orbs = ctx.manager.getCachedFromFetcher(fetcher, args);
    if (!useCache || orbs.length === 0) {
        orbs = yield ctx.manager.callFetcher(fetcher, args, useCache);
    }
    if (params.count && orbs.length > params.count) {
        orbs = orbs.slice(0, params.count);
    }
    SpraySystem.sprayOrbsRefactored((_b = (_a = ctx.orb.group) === null || _a === void 0 ? void 0 : _a.position.clone()) !== null && _b !== void 0 ? _b : new THREE.Vector3(), new THREE.Vector3(0, 0, 1), orbs, asSprayOptions(params, scene));
}));
function runSprayViaOrb(ctx, params) {
    var _a, _b;
    return __awaiter(this, void 0, void 0, function* () {
        const { byId, byType, first = true, fetcher, args, useCache = true, count } = params;
        if (!fetcher) {
            console.warn('[sprayViaOrb] Missing "fetcher"');
            return;
        }
        // Pick a target orb (e.g., BLACKHOLE) to act as the spray origin
        const manager = ctx.manager;
        let targets = [];
        if (byId) {
            const o = manager.getOrb(byId);
            if (o)
                targets.push(o);
        }
        if (byType) {
            targets.push(...manager.getOrbsByType(byType));
        }
        if (first && targets.length > 1) {
            targets = [targets[0]];
        }
        if (targets.length === 0) {
            console.warn('[sprayViaOrb] No target orb found (byId/byType)');
            return;
        }
        const originOrb = targets[0];
        // Resolve a scene using originOrb context (so spray renders where that orb lives)
        const scene = (() => {
            var _a;
            if (((_a = originOrb.group) === null || _a === void 0 ? void 0 : _a.parent) && originOrb.group.parent instanceof THREE.Scene) {
                return originOrb.group.parent;
            }
            const sceneName = manager.getSceneForOrb(originOrb);
            if (sceneName)
                return SceneManager.getInstance().getScene(sceneName);
            return global.scene;
        })();
        if (!scene) {
            console.warn('[sprayViaOrb] No scene available.');
            return;
        }
        // Fetch orbs (with cache if desired)
        let spawned = manager.getCachedFromFetcher(fetcher, args);
        if (!useCache || spawned.length === 0) {
            spawned = yield manager.callFetcher(fetcher, args, useCache);
        }
        if (count && spawned.length > count) {
            spawned = spawned.slice(0, count);
        }
        // Spray from the target orb position
        const originPos = (_b = (_a = originOrb.group) === null || _a === void 0 ? void 0 : _a.position.clone()) !== null && _b !== void 0 ? _b : new THREE.Vector3();
        SpraySystem.sprayOrbsRefactored(originPos, new THREE.Vector3(0, 0, 0), spawned, {
            sprayCoefficient: params.sprayCoefficient,
            timeGap: params.timeGap,
            baseVelocity: params.baseVelocity,
            drag: params.drag,
            scene,
        });
    });
}
actionRegistry.register('sprayViaOrb', (ctx, rawParams) => __awaiter(void 0, void 0, void 0, function* () {
    const params = resolveParams(rawParams, ctx);
    yield runSprayViaOrb(ctx, params);
}));
actionRegistry.register('sprayViaOrbHarnessed', (ctx, rawParams) => __awaiter(void 0, void 0, void 0, function* () {
    const params = resolveParams(rawParams, ctx);
    const fetcher = params.fetcher;
    if (!fetcher) {
        console.warn('[sprayViaOrbHarnessed] Missing "fetcher"');
        return;
    }
    const runtime = global.highPerformanceRuntime;
    if (!runtime) {
        yield runSprayViaOrb(ctx, params);
        return;
    }
    const queued = runtime.enqueueWorldOp(`sprayViaOrb:${fetcher}`, () => runSprayViaOrb(ctx, params));
    if (!queued) {
        console.warn('[sprayViaOrbHarnessed] Queue full, dropping request', {
            fetcher,
        });
    }
}));
// === New: callOrbAction (query-orbs, run their action) ===
actionRegistry.register('callOrbAction', (ctx, rawParams) => __awaiter(void 0, void 0, void 0, function* () {
    var _c, _d, _e;
    const params = resolveParams(rawParams, ctx);
    const action = (_c = params.action) !== null && _c !== void 0 ? _c : 'onActivate';
    // target selection
    let targets = [];
    if (params.byId) {
        const o = ctx.manager.getOrb(params.byId);
        if (o)
            targets.push(o);
    }
    if (params.byType) {
        targets.push(...ctx.manager.getOrbsByType(params.byType));
    }
    // optional filter by data field (simple equals)
    if (((_d = params.where) === null || _d === void 0 ? void 0 : _d.path) && ((_e = params.where) === null || _e === void 0 ? void 0 : _e.equals) !== undefined) {
        const path = params.where.path;
        const equals = params.where.equals;
        targets = targets.filter(o => {
            const v = path.split('.').reduce((acc, k) => (acc == null ? undefined : acc[k]), o.jsonData);
            return v === equals;
        });
    }
    if (params.first && targets.length > 1) {
        targets = [targets[0]];
    }
    if (targets.length === 0) {
        console.warn('[callOrbAction] No targets matched');
        return;
    }
    for (const t of targets) {
        yield t.runAction(action);
    }
}));
actionRegistry.register('connectOrbs', (ctx, raw) => __awaiter(void 0, void 0, void 0, function* () {
    var _f, _g, _h, _j, _k, _l, _m;
    const p = resolveParams(raw, ctx);
    const left = yield resolveOrbSet(ctx, (_g = (_f = p.left) !== null && _f !== void 0 ? _f : p.setA) !== null && _g !== void 0 ? _g : p.set);
    const right = p.right || p.setB ? yield resolveOrbSet(ctx, (_h = p.right) !== null && _h !== void 0 ? _h : p.setB) : left;
    const cartesian = !!p.cartesian || !p.right;
    const profileName = p.profileName;
    const profileInline = p.profile;
    const labelText = p.labelText;
    const connectionType = p.connectionType || 'edge';
    const cm = OrbConnectionManager.getInstance();
    const pairs = [];
    if (cartesian) {
        for (let i = 0; i < left.length; i++) {
            for (let j = i + 1; j < right.length; j++) {
                const a = left[i], b = right[j];
                if (((_j = a.group) === null || _j === void 0 ? void 0 : _j.uuid) === ((_k = b.group) === null || _k === void 0 ? void 0 : _k.uuid))
                    continue;
                pairs.push({ a, b, type: connectionType, profileName, profile: profileInline, labelText });
            }
        }
    }
    else {
        const n = Math.min(left.length, right.length);
        for (let i = 0; i < n; i++) {
            const a = left[i], b = right[i];
            if (!a || !b)
                continue;
            if (((_l = a.group) === null || _l === void 0 ? void 0 : _l.uuid) === ((_m = b.group) === null || _m === void 0 ? void 0 : _m.uuid))
                continue;
            pairs.push({ a, b, type: connectionType, profileName, profile: profileInline, labelText });
        }
    }
    cm.connectMany(pairs);
}));
//todo move somewhere else
const VALID_CT = ['edge', 'contain', 'orbit'];
function toConnectionType(v) {
    return VALID_CT.includes(v) ? v : 'edge';
}
actionRegistry.register('disconnectOrbs', (ctx, raw) => __awaiter(void 0, void 0, void 0, function* () {
    var _o, _p, _q, _r, _s;
    const p = resolveParams(raw, ctx);
    const left = yield resolveOrbSet(ctx, (_p = (_o = p.left) !== null && _o !== void 0 ? _o : p.setA) !== null && _p !== void 0 ? _p : p.set);
    const right = p.right || p.setB ? yield resolveOrbSet(ctx, (_q = p.right) !== null && _q !== void 0 ? _q : p.setB) : left;
    const cm = OrbConnectionManager.getInstance();
    const profileName = p.profileName;
    const connectionType = toConnectionType(p.connectionType);
    for (const a of left)
        for (const b of right) {
            if (((_r = a.group) === null || _r === void 0 ? void 0 : _r.uuid) === ((_s = b.group) === null || _s === void 0 ? void 0 : _s.uuid))
                continue;
            cm.disconnect(a, b, profileName, connectionType);
        }
}));
actionRegistry.register('connectByRelationships', (ctx, raw) => __awaiter(void 0, void 0, void 0, function* () {
    var _t, _u;
    const p = resolveParams(raw, ctx);
    const set = yield resolveOrbSet(ctx, (_t = p.set) !== null && _t !== void 0 ? _t : {});
    const cm = OrbConnectionManager.getInstance();
    const connectionType = toConnectionType(p.connectionType);
    for (const a of set)
        for (const b of set) {
            if (a === b)
                continue;
            const rels = a.relationships;
            const rel = rels === null || rels === void 0 ? void 0 : rels.find(r => r.targetOrbType && r.targetOrbType === b.orbType);
            if (!rel)
                continue;
            const profileInline = typeof rel.edgeProfile === 'string' ? undefined : rel.edgeProfile;
            const profileName = typeof rel.edgeProfile === 'string' ? rel.edgeProfile : (_u = rel.edgeProfile) === null || _u === void 0 ? void 0 : _u.name;
            cm.connect({ a, b, type: connectionType, profileName, profile: profileInline });
        }
}));
actionRegistry.register("connectPair", (_ctx, params) => __awaiter(void 0, void 0, void 0, function* () {
    var _v, _w, _x, _y, _z, _0;
    // expected params: { fromId, toId, profile? } OR { from, to }
    const from = (_v = params === null || params === void 0 ? void 0 : params.from) !== null && _v !== void 0 ? _v : (_x = (_w = global.orbManager) === null || _w === void 0 ? void 0 : _w.getOrb) === null || _x === void 0 ? void 0 : _x.call(_w, params.fromId);
    const to = (_y = params === null || params === void 0 ? void 0 : params.to) !== null && _y !== void 0 ? _y : (_0 = (_z = global.orbManager) === null || _z === void 0 ? void 0 : _z.getOrb) === null || _0 === void 0 ? void 0 : _0.call(_z, params.toId);
    if (!from || !to)
        return;
    connectOrbs({ from, to, profile: params.profile });
}));
actionRegistry.register('connectByProfiles', (ctx, raw) => __awaiter(void 0, void 0, void 0, function* () {
    var _1;
    const p = resolveParams(raw, ctx);
    const fromType = (_1 = p.fromType) !== null && _1 !== void 0 ? _1 : 'GITHUB_ORB';
    const profileLabel = p.profileLabel;
    if (!profileLabel)
        return;
    const cfg = OrbTypes[fromType];
    const profiles = cfg === null || cfg === void 0 ? void 0 : cfg.connectionProfiles;
    if (!profiles)
        return;
    const prof = profiles.find((x) => x.label === profileLabel);
    if (!prof)
        return;
    const manager = ctx.manager;
    const cm = OrbConnectionManager.getInstance();
    const fromOrbs = manager.getOrbsByType(fromType);
    const toOrbs = manager.getOrbsByType(prof.toType);
    // Map key -> ALL targets that share that key
    const toIndex = new Map();
    for (const o of toOrbs) {
        const kRaw = prof.buildTargetKey(o.jsonData);
        if (!kRaw)
            continue;
        const k = String(kRaw).trim().toLowerCase();
        const arr = toIndex.get(k);
        if (arr)
            arr.push(o);
        else
            toIndex.set(k, [o]);
    }
    for (const f of fromOrbs) {
        const skRaw = prof.buildSourceKey(f.jsonData);
        if (!skRaw)
            continue;
        const sk = String(skRaw).trim().toLowerCase();
        const matches = toIndex.get(sk);
        if (!matches || matches.length === 0)
            continue;
        for (const t of matches) {
            if (cm.has(f, t))
                continue;
            cm.connect({ a: f, b: t, type: 'edge', profile: prof.edgeProfile });
        }
    }
}));
function resolveOrbSet(ctx, sel) {
    return __awaiter(this, void 0, void 0, function* () {
        const out = [];
        const m = ctx.manager;
        if (sel === null || sel === void 0 ? void 0 : sel.ids) {
            for (const id of sel.ids) {
                const o = m.getOrb(id);
                if (o)
                    out.push(o);
            }
        }
        if (sel === null || sel === void 0 ? void 0 : sel.byType) {
            out.push(...m.getOrbsByType(sel.byType));
        }
        if (sel === null || sel === void 0 ? void 0 : sel.fetcher) {
            const { fetcher, args = {}, useCache = true } = sel;
            let orbs = m.getCachedFromFetcher(fetcher, args);
            if (!useCache || orbs.length === 0) {
                orbs = yield m.callFetcher(fetcher, args, useCache);
            }
            out.push(...orbs);
        }
        const seen = new Set();
        return out.filter(o => {
            var _a, _b;
            const id = (_b = (_a = o.group) === null || _a === void 0 ? void 0 : _a.uuid) !== null && _b !== void 0 ? _b : '';
            if (!id || seen.has(id))
                return false;
            seen.add(id);
            return true;
        });
    });
}
export function registerActionHandlers() { }
